import { useUserContext } from "../../providers/UserProvider";
import { useCsmOrganizations } from "../useCsmOrganizations/useCsmOrganizations";
import { ADMINCENTER_PRODUCT_ID } from "../../constants/constants";
import { isSuperAdmin } from "../../lib/scope-check";
import { IUserInstancePermission } from "../../domain/UserInstancePermission";

export interface SuperAdminOrganization {
    organizationId: string;
    organizationName: string;
}

/**
 * Derives every organization the current user is a super admin of, straight from their instance
 * permissions.
 *
 * This deliberately does NOT go through `useSupportOrganizations`: that hook joins accepted
 * invitations with instance permissions, so an org the user administers without a matching
 * invitation record (or whose invitation has aged past the auto-revoke window) never appears.
 * Callers that need the invitation-derived list — Support > Access — should keep using that hook.
 *
 * "Super admin" is the same test `UserProvider` applies to the current instance: unrestricted
 * (`All`) scope on the "Read Products" attribute, evaluated against the org's *Admin Center*
 * instance-permission roles. Unrestricted access on some other product does not qualify.
 *
 * No request is made here — `UserProvider` already fetches the unscoped instance permissions once
 * at boot and this reads them off the context.
 */
export const useSuperAdminOrganizations = () => {
    const { instancePermissions, instancePermissionsError } = useUserContext();
    const { hideFromSupportTableOrgs } = useCsmOrganizations();

    const csmOrgIds = hideFromSupportTableOrgs.map((o) => o.id);

    const permissionsByOrg = (instancePermissions || []).reduce(
        (byOrg: Map<string, IUserInstancePermission[]>, permission) => {
            const existing = byOrg.get(permission.organizationId);

            if (existing) {
                existing.push(permission);
            } else {
                byOrg.set(permission.organizationId, [permission]);
            }

            return byOrg;
        },
        new Map()
    );

    const organizations: SuperAdminOrganization[] = Array.from(permissionsByOrg.entries())
        .filter(([organizationId]) => !csmOrgIds.includes(organizationId))
        .filter(([, permissions]) => {
            const adminCenterRoles = permissions
                .filter((permission) => permission.productId === ADMINCENTER_PRODUCT_ID)
                .flatMap((permission) => permission.roles || []);

            return isSuperAdmin(adminCenterRoles);
        })
        .map(([organizationId, permissions]) => ({
            organizationId,
            organizationName: permissions.find((p) => !!p.organizationName)?.organizationName || ""
        }))
        .sort((a, b) => a.organizationName.localeCompare(b.organizationName, undefined, { sensitivity: "base" }));

    return {
        organizations,
        // `instancePermissions` stays `undefined` until the request settles. On failure it never
        // resolves, so fold the error in rather than reporting a load that never finishes.
        isLoading: instancePermissions === undefined && !instancePermissionsError,
        error: instancePermissionsError
    };
};
